import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import log from '../../logger';
import useItemListActions from '../../shared/cmsPage/hooks/useItemListActions';
import useArray from '../../shared/util/hooks/useArray';
import { selectIsSuperAdmin, selectUserEmail } from '../../store/rootSelectors';
import { errorGrowl } from '../../store/rootThunks';
import adminApi from '../api/adminApi';
import EditAdminModal from '../components/EditAdminModal';
import { AdminConstants } from '../definitions';
export default function useAdminActions(roles) {
  const dispatch = useDispatch();
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const email = useSelector(selectUserEmail);
  const { remove, requestPending } = useItemListActions(adminApi, AdminConstants, {
    excludePublishMsg: true,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const reloadRef = useRef(null);
  const close = async () => {
    setModalOpen(false);
    setAdmin(null);
    if (typeof reloadRef.current === 'function') await reloadRef.current();
  };

  const add = useMemo(
    () => ({
      label: '+ Add Admin',
      onClick: ({ reload }) => {
        setAdmin(null);
        setModalOpen(true);
        setIsNew(true);
        reloadRef.current = reload;
      },
    }),
    [isSuperAdmin]
  );
  const removeOverride = useMemo(
    () => ({
      ...remove,
      disabled: (item) =>
        item.user_email === email || (!isSuperAdmin && item.role === 'super_admin'),
    }),
    [remove, email]
  );

  const edit = useMemo(
    () => ({
      label: 'Edit',
      onClick: async (item, { reload }) => {
        setAdmin(item);
        setModalOpen(true);
        setIsNew(false);
        reloadRef.current = reload;
      },
      icon: 'fa-solid fa-pencil',
      disabled: (item) =>
        item.user_email === email || (!isSuperAdmin && item.role === 'super_admin'),
    }),
    [email, isSuperAdmin]
  );

  const resendInvite = useMemo(
    () => ({
      label: 'Resend Invite',
      icon: 'fa-solid fa-paper-plane',
      onClick: async (item) => {
        try {
          await adminApi.resendInvite(item);
        } catch (e) {
          log.error(e);
          dispatch(errorGrowl({ groupId: 'admins', props: e.message }));
        }
      },
      visible: (item) => item.invitePending,
    }),
    [dispatch]
  );

  const AdminModal = (
    <EditAdminModal
      isNew={isNew}
      open={modalOpen}
      onClose={close}
      admin={admin}
      roles={roles}
      saveAdmin={adminApi.saveRecord}
    />
  );
  const toolbarActions = useMemo(() => (isSuperAdmin ? [add] : []), [add, isSuperAdmin]);
  const itemActions = useArray(edit, resendInvite, removeOverride);

  return { toolbarActions, itemActions, requestPending, AdminModal };
}
